/**
 * EshopPricingSection.jsx
 * ------------------------------------------------------------------
 * Public pricing section for the four E-shop SEO/GEO tiers plus the
 * eight stackable add-ons. Renders as a self-contained section on
 * `/pricing` (look for `<EshopPricingSection />` near the bottom of
 * the page) — anchorable via `#eshop`.
 *
 * Interaction model:
 *   1. User picks ONE tier card (radio-style, mutually exclusive)
 *   2. User checks zero or more add-on rows underneath
 *   3. The "Subscribe" button at the bottom multiplies the tier
 *      price + every selected add-on → "€X / month".
 *   4. Click → POST /api/payments/checkout with `package_id=<tier>`
 *      and `?addons=...` query so the webhook activates everything.
 *
 * Why a single CTA at the bottom and not "Subscribe" per card?
 * Because forcing the user to confirm tier first means we can carry
 * their add-on selection through to Stripe in one go — no "pick tier,
 * pay, then back-track to pick add-ons" friction.
 */
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { API } from "../config";
import { Check, Star, Plus, Loader2, ArrowRight } from "lucide-react";

const LIMIT_LABELS = {
  eshops:                    "E-shopov",
  products_scanned_monthly:  "Produktov / mes",
  geo_pages_generated:       "GEO stránok",
  cities_in_geo_matrix:      "Miest v GEO matrici",
  ai_rank_checks_daily:      "AI rank checks / deň",
  industries:                "Industries",
  competitors_tracked:       "Konkurenti",
  blog_articles_monthly:     "Blog articles / mes",
  languages:                 "Jazyky",
};

// Tier limits we want to show as bullet rows on the card. Order matters
// — it's a marketing decision (lead with the metric that scales the
// most across tiers, not the most pedestrian one).
const HIGHLIGHT_KEYS = [
  "eshops",
  "products_scanned_monthly",
  "geo_pages_generated",
  "cities_in_geo_matrix",
  "ai_rank_checks_daily",
  "blog_articles_monthly",
  "languages",
];

export function EshopPricingSection({ userEmail }) {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTier, setSelectedTier] = useState("");
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/eshop-tiers`)
      .then(r => {
        const list = r.data.tiers || [];
        setTiers(list);
        // Pre-select the "popular" tier so the user doesn't land on
        // a totally inert pricing page.
        const popular = list.find(t => t.category === "tier" && t.popular);
        if (popular) setSelectedTier(popular.package_id);
      })
      .catch(() => setTiers([]))
      .finally(() => setLoading(false));
  }, []);

  const mainTiers = useMemo(() => tiers.filter(t => t.category === "tier"), [tiers]);
  const addons = useMemo(() => tiers.filter(t => t.category === "addon"), [tiers]);
  const lifetime = useMemo(() => tiers.find(t => t.category === "lifetime"), [tiers]);

  const totalMonthly = useMemo(() => {
    const tier = mainTiers.find(t => t.package_id === selectedTier);
    const base = tier ? Number(tier.price_eur || 0) : 0;
    const addonSum = selectedAddons.reduce((sum, id) => {
      const a = addons.find(x => x.package_id === id);
      return sum + (a ? Number(a.price_eur || 0) : 0);
    }, 0);
    return base + addonSum;
  }, [selectedTier, selectedAddons, mainTiers, addons]);

  function toggleAddon(id) {
    setSelectedAddons(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  async function startCheckout() {
    if (!selectedTier) return;
    if (!userEmail) {
      window.location.href = `/auth?next=${encodeURIComponent("/pricing#eshop")}&plan=${selectedTier}`;
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/payments/checkout`, {
        package_id: selectedTier,
        user_email: userEmail,
        addons: selectedAddons,                   // server passes these into metadata
        origin_url: window.location.origin,
        success_url: "/dashboard?payment=success&from=eshop",
        cancel_url: "/pricing#eshop",
      });
      window.location.href = r.data.url;
    } catch (e) {
      alert("Checkout failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <section id="eshop" className="py-20 flex items-center justify-center" data-testid="eshop-pricing-loading">
        <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
      </section>
    );
  }
  if (mainTiers.length === 0) return null;

  return (
    <section id="eshop" className="py-20 px-6" data-testid="eshop-pricing-section">
      <div className="max-w-7xl mx-auto">
        {/* ----- Header ----- */}
        <div className="text-center mb-12">
          <div className="text-xs uppercase tracking-widest text-purple-400 mb-3">
            Pre e-shopy
          </div>
          <h2 className="text-4xl sm:text-5xl font-black tracking-tight">
            E-shop SEO/GEO · vyber si veľkosť
          </h2>
          <p className="text-base text-gray-400 mt-4 max-w-2xl mx-auto">
            Štartovný pre lokálny shop · Profi pre národný · Business pre agentúry.
            Add-ony si pridáš keď ich potrebuješ — bez upgrade na vyšší tier.
          </p>
          <a
            href="/eshop-scanner"
            data-testid="eshop-pricing-try-scanner"
            className="inline-flex items-center gap-2 mt-6 px-5 py-2 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white text-sm font-bold rounded-sm"
          >
            Vyskúšaj E-shop Scanner →
          </a>
        </div>

        {/* ----- 4 tiers ----- */}
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4 mb-12" data-testid="eshop-pricing-tiers-grid">
          {mainTiers.map(t => (
            <TierCard
              key={t.package_id}
              tier={t}
              selected={selectedTier === t.package_id}
              onSelect={() => setSelectedTier(t.package_id)}
            />
          ))}
        </div>

        {/* ----- Add-ons ----- */}
        {selectedTier && (
          <div className="mb-12" data-testid="eshop-pricing-addons">
            <h3 className="text-xl font-bold mb-1 flex items-center gap-2">
              <Plus className="w-5 h-5 text-amber-400" />
              Rozšír si balíček (voliteľné)
            </h3>
            <p className="text-sm text-gray-400 mb-4">
              Stackujú sa na ktorýkoľvek tier. Vypneš ich kedykoľvek.
            </p>
            <div className="grid sm:grid-cols-2 gap-2">
              {addons.map(a => (
                <AddonRow
                  key={a.package_id}
                  addon={a}
                  checked={selectedAddons.includes(a.package_id)}
                  onToggle={() => toggleAddon(a.package_id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* ----- Total + CTA ----- */}
        {selectedTier && (
          <div className="sticky bottom-4 z-10 max-w-3xl mx-auto bg-gradient-to-r from-purple-900/80 to-gray-900/90 backdrop-blur-md border border-purple-700/50 rounded-sm p-5 flex items-center justify-between gap-4" data-testid="eshop-pricing-summary">
            <div>
              <div className="text-xs uppercase tracking-wider text-purple-300">
                Tvoja konfigurácia
              </div>
              <div className="text-3xl font-black mt-1 tabular-nums">
                €{totalMonthly.toFixed(0)}
                <span className="text-sm font-normal text-gray-400 ml-1">/ mes</span>
              </div>
              {selectedAddons.length > 0 && (
                <div className="text-xs text-gray-400 mt-1">
                  Tier + {selectedAddons.length} add-on{selectedAddons.length > 1 ? "ov" : ""}
                </div>
              )}
            </div>
            <button
              data-testid="eshop-pricing-checkout-btn"
              disabled={submitting}
              onClick={startCheckout}
              className="px-6 py-3 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white font-bold rounded-sm flex items-center gap-2"
            >
              {submitting
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Spracovávam…</>
                : <>Spustiť za €{totalMonthly.toFixed(0)} <ArrowRight className="w-4 h-4" /></>
              }
            </button>
          </div>
        )}

        {/* ----- Lifetime banner ----- */}
        {lifetime && (
          <div className="mt-12 p-6 bg-gradient-to-r from-amber-900/30 to-amber-900/10 border border-amber-700/50 rounded-sm flex items-center justify-between gap-4" data-testid="eshop-pricing-lifetime">
            <div>
              <div className="text-xs uppercase tracking-wider text-amber-400 mb-1">
                Lifetime Deal · jednorázovo
              </div>
              <h4 className="font-bold text-lg">{lifetime.name}</h4>
              <p className="text-sm text-gray-400 mt-1">{lifetime.tagline}</p>
            </div>
            <div className="text-right shrink-0">
              <div className="text-3xl font-black tabular-nums">€{Number(lifetime.price_eur).toFixed(0)}</div>
              <div className="text-xs text-gray-500">platba raz, navždy</div>
              <button
                data-testid="eshop-pricing-lifetime-btn"
                onClick={() => { setSelectedTier(lifetime.package_id); setSelectedAddons([]); }}
                className="mt-2 px-4 py-2 bg-amber-600 hover:bg-amber-500 text-black text-sm font-bold rounded-sm"
              >
                Vybrať Lifetime
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function TierCard({ tier, selected, onSelect }) {
  const limits = tier.limits || {};
  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={`eshop-pricing-tier-card-${tier.package_id}`}
      onClick={onSelect}
      onKeyDown={e => (e.key === "Enter" || e.key === " ") && onSelect()}
      className={`p-5 border rounded-sm cursor-pointer transition-all ${
        selected
          ? "border-purple-500 bg-purple-900/20 ring-2 ring-purple-500/40 -translate-y-1"
          : "border-gray-800 bg-gray-900/40 hover:border-purple-700"
      } ${tier.popular ? "relative" : ""}`}
    >
      {tier.popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-purple-600 text-white text-xs font-bold rounded-sm flex items-center gap-1">
          <Star className="w-3 h-3 fill-white" /> NAJOBĽÚBENEJŠÍ
        </div>
      )}
      <div className="font-bold text-lg">{tier.name?.replace("E-shop SEO · ", "") || tier.name}</div>
      <div className="text-xs text-gray-400 mt-1 min-h-[2.5em]">{tier.tagline}</div>
      <div className="flex items-baseline gap-1 mt-4 border-t border-gray-800 pt-4">
        <span className="text-gray-500">€</span>
        <span className="text-4xl font-black tabular-nums">{Number(tier.price_eur).toFixed(0)}</span>
        <span className="text-xs text-gray-500 ml-1">/ mes</span>
      </div>
      <ul className="mt-5 space-y-1.5 text-sm">
        {HIGHLIGHT_KEYS.filter(k => limits[k]).map(k => (
          <li key={k} className="flex items-center gap-2">
            <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
            <span className="text-gray-200 tabular-nums">
              {limits[k] >= 1000 ? `${(limits[k] / 1000).toFixed(limits[k] % 1000 ? 1 : 0)}k` : limits[k]}
            </span>
            <span className="text-gray-400">{LIMIT_LABELS[k]}</span>
          </li>
        ))}
        {limits.white_label && (
          <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" /><span className="text-gray-200">White-label</span></li>
        )}
        {limits.api_access && (
          <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" /><span className="text-gray-200">REST API</span></li>
        )}
      </ul>
    </div>
  );
}

function AddonRow({ addon, checked, onToggle }) {
  return (
    <label
      data-testid={`eshop-pricing-addon-${addon.package_id}`}
      className={`flex items-center gap-3 p-3 border rounded-sm cursor-pointer transition-colors ${
        checked
          ? "border-purple-500 bg-purple-900/20"
          : "border-gray-800 bg-gray-900/40 hover:border-purple-700/50"
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className="accent-purple-500"
      />
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm">{addon.name}</div>
        <div className="text-xs text-gray-400">{addon.tagline}</div>
      </div>
      <div className="text-sm font-bold tabular-nums shrink-0">
        +€{Number(addon.price_eur).toFixed(0)}/mes
      </div>
    </label>
  );
}

export default EshopPricingSection;
